import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import { ArrowLeft, Check, Copy, CopyPlus, LogIn, Users } from 'lucide-react'
import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { ClampText } from '@/components/clamp-text'
import { SkeletonPlan } from '@/components/list-skeletons'
import { PlanOutline } from '@/components/plan-outline'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { CATEGORY_COLORS, categoryTextColor } from '@/lib/category-color'
import { goalSlug as slugFor } from '@/lib/utils'

// Parallax cover media: oversized + masked to fade out at the bottom, matching
// the goal-details header. Transform is driven by scroll for the parallax.
const COVER_STYLE: CSSProperties = {
  top: '-20%',
  height: '140%',
  willChange: 'transform',
  maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
  WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
}

function titleFromSlug(slug: string): string {
  const words = slug.split('-').slice(0, -1)
  if (!words.length) return 'Goal'
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

const CONVEX_SITE_URL =
  import.meta.env.VITE_CONVEX_SITE_URL ?? 'https://brazen-mammoth-385.convex.site'

export const Route = createFileRoute('/share/$goalId')({
  head: ({ params }) => {
    const title = titleFromSlug(params.goalId)
    const goalId = params.goalId.split('-').at(-1) ?? ''
    const ogImage = `${CONVEX_SITE_URL}/og/goal?title=${encodeURIComponent(title)}&goalId=${goalId}`
    return {
      meta: [
        { title: `${title} — Goaltree` },
        { property: 'og:title', content: `${title} — Goaltree` },
        { property: 'og:image', content: ogImage },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:type', content: 'image/png' },
        { property: 'og:type', content: 'website' },
        { name: 'twitter:image', content: ogImage },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: `${title} — Goaltree` },
      ],
    }
  },
  validateSearch: (search: Record<string, unknown>) => ({
    edit: search.edit === 'true' || search.edit === true,
  }),
  component: RouteComponent,
})

type PublicGoal = NonNullable<ReturnType<typeof useQuery<typeof api.nodes.getPublicGoalTree>>>

function SharePageContent({
  goal,
  goalId,
  goalSlug,
  isAuthenticated,
  editMode,
}: {
  goal: PublicGoal
  goalId: Id<'nodes'>
  goalSlug: string
  isAuthenticated: boolean
  editMode: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const [copied, setCopied] = useState(false)
  const [joining, setJoining] = useState(false)
  const joinByGoalId = useMutation(api.sharing.joinByGoalId)
  const copyPublicGoal = useMutation(api.nodes.copyPublicGoal)
  const [copying, setCopying] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  const allTargets = goal.milestones.flatMap((ms) => ms.targets)
  const allTasks = allTargets.flatMap((tg) => tg.tasks)
  const completedCount = allTasks.filter((t) => t.completedAt !== null).length
  const pct =
    allTasks.length > 0 ? Math.round((completedCount / allTasks.length) * 100) : goal.progress
  const color = CATEGORY_COLORS[goal.category.toLowerCase()] ?? '#6b7280'

  // Parallax cover, matching the goal-details header: the media drifts as the
  // page scrolls. Cover media is <img> or <video>; both are HTMLElements.
  const mainRef = useRef<HTMLElement | null>(null)
  const coverRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!goal.coverUrl) return
    const scrollEl = mainRef.current
    if (!scrollEl) return
    const onScroll = () => {
      if (coverRef.current) {
        coverRef.current.style.transform = `translateY(${scrollEl.scrollTop * 0.35}px)`
      }
    }
    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', onScroll)
  }, [goal.coverUrl])

  // Once the title block scrolls away, the header carries the goal's title and progress.
  const titleRef = useRef<HTMLDivElement>(null)
  const [past, setPast] = useState(false)
  useEffect(() => {
    const el = titleRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setPast(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { root: mainRef.current },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  async function handleCopy() {
    await navigator.clipboard.writeText(
      withShareUtm(`https://goaltree.ai/share/${goalSlug}`, 'goal_link'),
    )
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleUsePlan() {
    setCopying(true)
    try {
      const newId = await copyPublicGoal({ goalId })
      toast.success(t('leaderboard.profile.planCopied'))
      navigate({ to: '/goals/$goalId', params: { goalId: slugFor(goal.title, newId) } })
    } catch (err) {
      if (err instanceof ConvexError && String(err.data).includes('Free plan is limited'))
        setUpgradeOpen(true)
      else toast.error(t('leaderboard.profile.copyFailed'))
    } finally {
      setCopying(false)
    }
  }

  async function handleJoin() {
    setJoining(true)
    try {
      const id = await joinByGoalId({ goalId })
      if (id) navigate({ to: '/goals/$goalId', params: { goalId: id as string } })
    } finally {
      setJoining(false)
    }
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      <header className="relative shrink-0 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {(canGoBack || isAuthenticated) && (
              <button
                type="button"
                onClick={() => (canGoBack ? router.history.back() : navigate({ to: '/today' }))}
                aria-label={t('common.back')}
                className="-ml-2 flex size-9 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowLeft className="size-5" />
              </button>
            )}
            {past ? (
              <div className="flex min-w-0 flex-col animate-in fade-in-0 slide-in-from-bottom-1">
                <span className="truncate text-sm font-bold leading-tight">{goal.title}</span>
                {allTasks.length > 0 && (
                  <span className="font-mono text-[11px] leading-tight tabular-nums text-muted-foreground">
                    {completedCount}/{allTasks.length} · {pct}%
                  </span>
                )}
              </div>
            ) : (
              <>
                <img src="/favicon.svg?v=4" alt="Goaltree" className="h-7 w-7 select-none" />
                <span className="text-sm font-semibold tracking-tight">Goaltree</span>
              </>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
            >
              {copied ? (
                <Check className="h-3 w-3 text-primary-text" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
              {copied ? t('share.copied') : t('share.copyLink')}
            </button>
            {editMode ? (
              isAuthenticated ? (
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={joining}
                  className="cursor-pointer flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  <Users className="h-3 w-3" />
                  {joining ? '…' : t('share.startCollaborating')}
                </button>
              ) : (
                <Link
                  to="/auth/login"
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <LogIn className="h-3 w-3" />
                  {t('share.signInToEdit')}
                </Link>
              )
            ) : isAuthenticated ? (
              <button
                type="button"
                onClick={handleUsePlan}
                disabled={copying}
                className="cursor-pointer flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                <CopyPlus className="h-3 w-3" />
                {copying ? '…' : t('leaderboard.profile.usePlan')}
              </button>
            ) : (
              <Link
                to="/auth/login"
                className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <LogIn className="h-3 w-3" />
                {t('share.signIn')}
              </Link>
            )}
          </div>
        </div>
        {past && allTasks.length > 0 && (
          <div className="absolute inset-x-0 -bottom-px h-[2px] bg-muted">
            <div
              className="h-full bg-primary transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </header>

      <main ref={mainRef} className="flex-1 overflow-y-auto">
        <div className="relative">
          {goal.coverUrl && (
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              {goal.coverIsVideo ? (
                <video
                  ref={(el) => {
                    coverRef.current = el
                  }}
                  src={goal.coverUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute left-0 w-full object-cover"
                  style={COVER_STYLE}
                />
              ) : (
                <img
                  ref={(el) => {
                    coverRef.current = el
                  }}
                  src={goal.coverUrl}
                  alt=""
                  className="absolute left-0 w-full object-cover"
                  style={COVER_STYLE}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-background" />
            </div>
          )}
          <div
            ref={titleRef}
            className={cn(
              'relative z-10 mx-auto w-full max-w-3xl px-4 pb-4',
              goal.coverUrl ? 'pt-16' : 'pt-6',
            )}
            style={
              goal.coverUrl
                ? ({
                    '--foreground': 'oklch(1 0 0)',
                    '--muted-foreground': 'oklch(0.8 0 0)',
                  } as CSSProperties)
                : undefined
            }
          >
            <p
              className="mb-1 text-xs font-semibold uppercase tracking-widest"
              style={{ color: categoryTextColor(color) }}
            >
              {t(`goals.cat.${goal.category.toLowerCase()}`, { defaultValue: goal.category })}
            </p>
            <ClampText>
              {(clamp) => (
                <h1 className={cn('text-3xl font-black leading-tight text-foreground', clamp)}>
                  {goal.title}
                </h1>
              )}
            </ClampText>
            {goal.ownerName &&
              (goal.ownerHandle ? (
                <a
                  href={`https://gt.today/${goal.ownerHandle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-2 flex w-fit items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <UserAvatar
                    image={goal.ownerImage}
                    name={goal.ownerName}
                    size={20}
                    className="h-5 w-5 shrink-0 rounded-full object-cover"
                  />
                  <span className="text-sm underline-offset-2 group-hover:underline">
                    {goal.ownerName}
                  </span>
                </a>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <UserAvatar
                    image={goal.ownerImage}
                    name={goal.ownerName}
                    size={20}
                    className="h-5 w-5 shrink-0 rounded-full object-cover"
                  />
                  <span className="text-sm text-muted-foreground">{goal.ownerName}</span>
                </div>
              ))}
            {allTasks.length > 0 && (
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t('goals.remaining', { count: allTasks.length - completedCount })}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">
                    {t('goals.taskCount', { completed: completedCount, total: allTasks.length })}
                  </span>
                  <span className="shrink-0 text-xs font-bold text-primary-text">{pct}%</span>
                </div>
              </div>
            )}
            {allTasks.length > 0 && (
              <div className="relative mt-3 h-[3px] rounded-full bg-muted">
                <div
                  className="absolute left-0 top-0 h-full rounded-full transition-[width] duration-500"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: 'var(--primary)',
                    boxShadow: '0 0 8px var(--primary)',
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-4 pb-6">
          <PlanOutline
            tracked
            sticky
            milestones={goal.milestones.map((m) => ({
              key: m._id,
              title: m.title,
              targets: m.targets.map((tg) => ({
                key: tg._id,
                title: tg.title,
                tasks: tg.tasks.map((tk) => ({
                  key: tk._id,
                  title: tk.title,
                  description: tk.description,
                  effort: tk.effort,
                  done: tk.completedAt !== null,
                })),
              })),
            }))}
          />

          {!isAuthenticated && (
            <div className="mt-8 rounded-xl border bg-muted/40 px-4 py-6 text-center">
              <p className="text-sm font-semibold">{t('share.wantToTrack')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('share.wantToTrackDesc')}</p>
              <Link
                to="/auth/signup"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t('share.getStarted')}
              </Link>
            </div>
          )}
        </div>
      </main>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="goal-limit" />
    </div>
  )
}

function RouteComponent() {
  const { t } = useTranslation()
  const { goalId: goalSlug } = Route.useParams()
  const { edit: editMode } = Route.useSearch()
  const lastSegment = goalSlug.split('-').pop() ?? goalSlug
  const isValidId = lastSegment.length >= 20 && /^[a-z0-9]+$/.test(lastSegment)
  const convexId = lastSegment as Id<'nodes'>
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth()
  const navigate = useNavigate()

  const ownerTree = useQuery(
    api.nodes.getGoalTree,
    isAuthenticated && isValidId ? { goalId: convexId } : 'skip',
  )

  // Edit links stay here so the viewer can accept the collaboration invite.
  const toFriendView = isAuthenticated && !editMode
  useEffect(() => {
    if (ownerTree != null) {
      navigate({ to: '/goals/$goalId', params: { goalId: goalSlug }, replace: true })
    } else if (ownerTree === null && toFriendView) {
      navigate({ to: '/friends/goals/$goalId', params: { goalId: goalSlug }, replace: true })
    }
  }, [ownerTree, toFriendView, goalSlug, navigate])

  const goal = useQuery(api.nodes.getPublicGoalTree, isValidId ? { goalId: convexId } : 'skip')

  // Once the page has fully loaded, don't flash skeleton on re-focus (Convex
  // reconnect / auth recheck). For logged-out viewers the ownership query is
  // skipped (stays undefined), so gate on !isAuthenticated instead of === null —
  // otherwise the flag never flips and the skeleton flashes on every focus.
  const initialLoadDone = useRef(false)
  if (
    !authLoading &&
    goal !== undefined &&
    (!isAuthenticated || (ownerTree === null && !toFriendView))
  ) {
    initialLoadDone.current = true
  }

  const skeleton = (
    <div className="flex h-screen flex-col bg-background">
      <header className="shrink-0 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/favicon.svg?v=4" alt="Goaltree" className="h-7 w-7 select-none" />
            <span className="text-sm font-semibold tracking-tight">Goaltree</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-24 animate-pulse rounded-lg bg-muted" />
            <div className="h-7 w-20 animate-pulse rounded-lg bg-muted" />
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 pt-6 pb-4">
          <div className="mb-1 flex h-4 items-center">
            <div className="h-2.5 w-16 animate-pulse rounded bg-muted" />
          </div>
          <div className="flex h-[38px] items-center">
            <div className="h-7 w-3/5 animate-pulse rounded-lg bg-muted" />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="size-5 animate-pulse rounded-full bg-muted" />
            <div className="h-3 w-24 animate-pulse rounded bg-muted/60" />
          </div>
          <div className="mt-3 flex h-4 items-center justify-between">
            <div className="h-2.5 w-24 animate-pulse rounded bg-muted/60" />
            <div className="h-2.5 w-16 animate-pulse rounded bg-muted/60" />
          </div>
          <div className="mt-3 h-[3px] rounded-full bg-muted" />
        </div>
        <div className="mx-auto max-w-3xl px-4 pb-6">
          <SkeletonPlan />
        </div>
      </main>
    </div>
  )

  if (!isValidId) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <p className="text-lg font-semibold">{t('goals.notFound')}</p>
        <p className="text-sm text-muted-foreground">{t('share.goalDeleted')}</p>
        <Link
          to="/"
          className="text-sm font-medium text-primary-text underline-offset-4 hover:underline"
        >
          {t('share.goToGoaltree')}
        </Link>
      </div>
    )
  }

  if (!initialLoadDone.current) {
    if (authLoading || (isAuthenticated && ownerTree === undefined)) return skeleton
    if (ownerTree != null || toFriendView) return skeleton
    if (goal === undefined) return skeleton
  }

  if (goal == null) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <p className="text-lg font-semibold">{t('goals.notFound')}</p>
        <p className="text-sm text-muted-foreground">{t('share.goalDeleted')}</p>
        <Link
          to="/"
          className="text-sm font-medium text-primary-text underline-offset-4 hover:underline"
        >
          {t('share.goToGoaltree')}
        </Link>
      </div>
    )
  }

  return (
    <SharePageContent
      goal={goal}
      goalId={convexId}
      goalSlug={goalSlug}
      isAuthenticated={isAuthenticated}
      editMode={editMode}
    />
  )
}
